import { CsvButton } from "@/components/CsvButton";
import { downloadCsv, today } from "@/lib/csv";
import React from "react";
import axios from "axios";
import { Loader2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PlayerName } from "@/components/PlayerCardDialog";
import { ARCHETYPES } from "@/components/ArchetypeBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const XG = [["hd_xg", "HD xG", "High-danger xGoals: chances close to the net or from dangerous areas (most important for scoring)"],
  ["md_xg", "MD xG", "Medium-danger xGoals: reasonable chances, less favorable than high-danger"],
  ["ld_xg", "LD xG", "Low-danger xGoals: shots from farther out or poor angles"]];
const TIERS = [["ALL", "All HD xG"], ["TOP", "Top third HD xG"], ["MID", "Middle third HD xG"], ["LOW", "Bottom third HD xG"]];
const xv = (p, k, perGame) => !p.xg ? null : perGame ? (p.xg.gp ? +(p.xg[k] / p.xg.gp).toFixed(3) : null) : p.xg[k];
const BOOKS = ["DraftKings", "FanDuel", "BetMGM", "Caesars", "BetRivers", "Fanatics", "theScore", "Hard Rock", "Betr", "Circa"];

export default function AtgOddsTable({ apiDate }) {
  const [d, setD] = React.useState(null);
  const [err, setErr] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [valueOnly, setValueOnly] = React.useState(false);
  const [sort, setSort] = React.useState("implied");
  const [arch, setArch] = React.useState("ALL");
  const [perGame, setPerGame] = React.useState(false);
  const [tier, setTier] = React.useState("ALL");

  const load = React.useCallback(() => {
    setErr(false);
    axios.get(`${API}/atg-odds`, { params: apiDate ? { date: apiDate } : {}, timeout: 240000 })
      .then((r) => setD(r.data)).catch(() => setErr(true));
  }, [apiDate]);
  React.useEffect(() => { load(); const t = setInterval(load, 10 * 60 * 1000); return () => clearInterval(t); }, [load]);

  const cuts = React.useMemo(() => {
    const v = (d?.players || []).map((p) => xv(p, "hd_xg", perGame)).filter((x) => x != null).sort((a, b) => a - b);
    return v.length ? [v[Math.floor(v.length / 3)], v[Math.floor((2 * v.length) / 3)]] : null;
  }, [d, perGame]);
  const inTier = React.useCallback((p) => {
    if (tier === "ALL") return true;
    const v = xv(p, "hd_xg", perGame);
    if (v == null || !cuts) return false;
    return tier === "TOP" ? v >= cuts[1] : tier === "MID" ? v >= cuts[0] && v < cuts[1] : v < cuts[0];
  }, [tier, perGame, cuts]);
  const rows = React.useMemo(() => {
    const ql = q.trim().toLowerCase();
    const val = (p) => (sort.endsWith("_xg") ? xv(p, sort, perGame) : p[sort]) ?? -99;
    return (d?.players || [])
      .filter((p) => (!valueOnly || p.value) && (arch === "ALL" || p.archetype === arch) && inTier(p) &&
        (!ql || p.name.toLowerCase().includes(ql) || p.team.toLowerCase().includes(ql)))
      .sort((a, b) => val(b) - val(a));
  }, [d, q, valueOnly, sort, arch, perGame, inTier]);
  const counts = React.useMemo(() => (d?.players || []).reduce((m, p) => ({ ...m, [p.archetype]: (m[p.archetype] || 0) + 1 }), {}), [d]);

  return (
    <section className="rounded-xl border border-emerald-900/40 bg-slate-900/30 p-3" data-testid="atg-odds-table">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-bold uppercase tracking-wide text-emerald-200">Anytime Goal Odds</h3>
          <p className="font-mono text-[10px] text-slate-500">
            Best price + implied % · VALUE = model edge ≥ 3 pts
            {d?.xg_source?.ok && <> · xG: {d.xg_source.source} {d.xg_source.season}-{String(d.xg_source.season + 1).slice(2)} season, all situations</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-500" />
            <Input data-testid="atg-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Player or team" className="w-44 border-slate-800 bg-slate-900 pl-8 font-mono text-xs" />
          </div>
          <select data-testid="atg-archetype-filter" value={arch} onChange={(e) => setArch(e.target.value)} className="h-9 max-w-[220px] rounded-md border border-slate-800 bg-slate-900 px-2 font-mono text-xs text-fuchsia-200">
            <option value="ALL">All archetypes ({d?.players?.length || 0})</option>
            {Object.keys(ARCHETYPES).map((k) => <option key={k} value={k}>{ARCHETYPES[k].icon} {k} ({counts[k] || 0})</option>)}
            <option value="NO ARCHETYPE">No archetype ({counts["NO ARCHETYPE"] || 0})</option>
            <option value="UNVERIFIED">Unverified ({counts.UNVERIFIED || 0})</option>
          </select>
          <select data-testid="atg-xg-tier" value={tier} onChange={(e) => setTier(e.target.value)} className="h-9 rounded-md border border-slate-800 bg-slate-900 px-2 font-mono text-xs text-sky-200"
            title={cuts ? `Thirds of tonight's players by HD xG${perGame ? " per game" : ""}: below ${cuts[0]} · ${cuts[0]}–${cuts[1]} · ${cuts[1]}+` : ""}>
            {TIERS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <div className="flex h-9 overflow-hidden rounded-md border border-slate-800 font-mono text-[11px] font-bold">
            {[[false, "Season"], [true, "Per game"]].map(([v, l]) => (
              <button key={l} data-testid={`atg-xg-mode-${v ? "pergame" : "season"}`} onClick={() => setPerGame(v)}
                className={`px-2.5 transition-colors ${perGame === v ? "bg-sky-600/25 text-sky-200" : "bg-slate-900 text-slate-500 hover:text-slate-300"}`}>{l}</button>
            ))}
          </div>
          <select data-testid="atg-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 rounded-md border border-slate-800 bg-slate-900 px-2 font-mono text-xs text-slate-200">
            <option value="implied">Sort: implied %</option>
            <option value="edge">Sort: model edge</option>
            <option value="model">Sort: model %</option>
            <option value="score">Sort: CYPHER score</option>
            {XG.map(([k, l]) => <option key={k} value={k}>Sort: {l}</option>)}
          </select>
          <CsvButton label="Odds CSV" testId="csv-atg-odds" onClick={() => downloadCsv(`anytime-goal-odds_${today()}.csv`,
            rows.map((p) => ({ player: p.name, team: p.team, opponent: p.opp, slot: p.slot, best: p.best, best_book: p.best_book,
              implied_pct: p.implied, model_pct: p.model, edge: p.edge, value: p.value ? "YES" : "", cypher_score: p.score,
              archetype: p.archetype, games_played: p.xg?.gp, ...Object.fromEntries(XG.map(([k]) => [`${k}${perGame ? "_per_game" : ""}`, xv(p, k, perGame)])), ...p.books })))} />
          <button data-testid="atg-value-only" onClick={() => setValueOnly((v) => !v)}
            className={`h-9 rounded-md border px-3 font-mono text-[11px] font-bold transition-colors ${valueOnly ? "border-emerald-600 bg-emerald-600/20 text-emerald-200" : "border-slate-800 bg-slate-900 text-slate-400 hover:text-slate-200"}`}>
            VALUE only
          </button>
        </div>
      </div>
      {err ? (
        <p className="py-4 text-center font-mono text-[11px] text-rose-400" data-testid="atg-error">Anytime goal odds unavailable <button onClick={load} className="ml-2 underline">Retry</button></p>
      ) : !d ? (
        <div className="flex items-center gap-2 py-6 font-mono text-[11px] text-slate-500"><Loader2 className="h-4 w-4 animate-spin text-emerald-400" /> Pulling anytime goal odds…</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-800">
          <table className="w-full font-mono text-[11px]">
            <thead className="bg-slate-900 text-[9px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="sticky left-0 z-10 bg-slate-900 px-2 py-2 text-left">Player</th>
                <th className="px-2 py-2 text-left">Game</th>
                <th className="px-2 py-2 text-right">Best</th>
                <th className="px-2 py-2 text-right">Impl %</th>
                <th className="px-2 py-2 text-right">Model %</th>
                <th className="px-2 py-2 text-right">Edge</th>
                <th className="px-2 py-2 text-left">Archetype</th>
                {XG.map(([k, l, tip]) => <th key={k} title={tip} className={`whitespace-nowrap px-2 py-2 text-right ${k === "hd_xg" ? "text-sky-300" : ""}`}>{l}{perGame ? "/GP" : ""}</th>)}
                {BOOKS.map((b) => <th key={b} className="whitespace-nowrap px-2 py-2 text-right">{b}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={`${p.team}-${p.name}`} className={`border-t border-slate-800/60 transition-colors hover:bg-emerald-950/20 ${p.value ? "bg-emerald-950/10" : ""}`} data-testid={`atg-row-${p.team}-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <td className="sticky left-0 z-10 whitespace-nowrap bg-slate-950 px-2 py-1.5">
                    <PlayerName name={p.name} team={p.team} className="font-bold text-slate-100" />
                    {p.value && <span className="ml-1.5 rounded bg-emerald-600 px-1 text-[8px] font-bold text-white">VALUE</span>}
                  </td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-slate-400">{p.team} vs {p.opp}{p.slot && <span className="ml-1 text-slate-600">{p.slot}</span>}</td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-right font-bold text-emerald-300">{p.best} <span className="text-[9px] font-normal text-slate-500">{p.best_book}</span></td>
                  <td className="px-2 py-1.5 text-right text-slate-200">{p.implied}</td>
                  <td className="px-2 py-1.5 text-right text-slate-300">{p.model ?? "—"}</td>
                  <td className={`px-2 py-1.5 text-right font-bold ${p.edge == null ? "text-slate-600" : p.edge >= 3 ? "text-emerald-300" : p.edge < 0 ? "text-rose-400" : "text-slate-300"}`}>{p.edge == null ? "—" : `${p.edge > 0 ? "+" : ""}${p.edge}`}</td>
                  <td className="whitespace-nowrap px-2 py-1.5 text-fuchsia-300"><span title={ARCHETYPES[p.archetype]?.rule}>{ARCHETYPES[p.archetype] ? `${ARCHETYPES[p.archetype].icon} ${p.archetype}` : <span className="text-slate-600">{p.archetype === "UNVERIFIED" ? "Unverified" : "—"}</span>}</span></td>
                  {XG.map(([k]) => {
                    const v = xv(p, k, perGame);
                    return <td key={k} data-testid={`atg-${k}-${p.team}`} title={p.xg ? `${p.xg.gp} GP` : "No MoneyPuck data"} className={`px-2 py-1.5 text-right tabular-nums ${v == null ? "text-slate-600" : k === "hd_xg" ? "font-bold text-sky-300" : "text-slate-300"}`}>{v == null ? "—" : v.toFixed(2)}</td>;
                  })}
                  {BOOKS.map((b) => (
                    <td key={b} className={`px-2 py-1.5 text-right ${p.books[b] && p.books[b] === p.best ? "font-bold text-emerald-300" : "text-slate-400"}`}>{p.books[b] || "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {!rows.length && <p className="py-4 text-center font-mono text-[11px] text-slate-600" data-testid="atg-empty">No odds match</p>}
        </div>
      )}
    </section>
  );
}
